import { AwsRum, type AwsRumConfig } from 'aws-rum-web';

const REGION = 'us-east-2';

type RumIdentifiers = {
  applicationId: string;
  identityPoolId: string;
};

// One bundle deploys to every environment, so the hostname selects the identifiers. Each entry
// comes from the rum_app_monitor_id and rum_identity_pool_id outputs of that workspace's own
// state. They are public by design — they ship inside the JS bundle, and the guest role they
// unlock is scoped to rum:PutRumEvents on one app monitor. An entry stays null
// until that workspace has been applied, which leaves RUM uninitialised rather than starting it
// with identifiers that resolve to nothing. A Map, not an object, so that a hostname like
// "constructor" cannot inherit a value.
export const RUM_BY_HOSTNAME = new Map<string, RumIdentifiers | null>([
  [
    'app.gofari.com',
    {
      applicationId: '7c6af805-2fda-42ae-a72f-c3fa4428f616',
      identityPoolId: 'us-east-2:091ca964-c38e-4b49-bb10-e752a90783b1',
    },
  ],
]);

export function rumIdentifiers(hostname: string): RumIdentifiers | null {
  return RUM_BY_HOSTNAME.get(hostname) ?? null;
}

export function initRum(): void {
  const identifiers = rumIdentifiers(window.location.hostname);
  if (!identifiers) {
    return;
  }

  // guestRoleArn is deliberately absent: passing it switches the client to Cognito's basic
  // (classic) authflow, which the identity pool does not allow. Without it the client uses the
  // enhanced flow and picks up the unauthenticated role from the pool's role attachment.
  const config: AwsRumConfig = {
    identityPoolId: identifiers.identityPoolId,
    sessionSampleRate: 1.0,
    // 'http' is deliberately excluded: it records full request URLs, and a Magic Link carries a
    // live token as a query parameter.
    telemetries: ['errors', 'performance'],
    enableXRay: false,
    // Belt and braces for the same reason — a future tokenised route is scrubbed by default.
    recordResourceUrl: false,
    pageIdFormat: 'PATH',
  };

  try {
    new AwsRum(identifiers.applicationId, '1.0.0', REGION, config);
  } catch {
    // RUM must never take the app down with it.
  }
}
